import { Component, OnInit, inject } from '@angular/core';
import { FormControl, FormGroup, UntypedFormControl, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Guid } from 'guid-typescript';
import { InterestRateModel } from 'src/app/models/interestRate.model';
import { SettingsModel } from 'src/app/models/settings.model';
import { BudgetService } from 'src/app/services/budget.service';
import { SettingsService } from 'src/app/services/settings.service';
import { ToastService } from 'src/app/services/toast.service';
import { CommonFunctionsConfiguratorService } from 'src/app/shared/services/swal-fire-configurator.service';
import { MatFormField, MatInput, MatError } from '@angular/material/input';
import { NgClass } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { MatButton, MatIconButton } from '@angular/material/button';
import { PageHeaderComponent } from 'src/app/shared/ui/page-header/page-header.component';
import { EmptyStateComponent } from 'src/app/shared/ui/empty-state/empty-state.component';

@Component({
    selector: 'app-interest-rate',
    templateUrl: './interest-rate.component.html',
    styleUrls: ['./interest-rate.component.scss'],
    imports: [FormsModule, ReactiveFormsModule, MatFormField, MatInput, MatError, NgClass, MatIcon, MatIconButton, MatButton, PageHeaderComponent, EmptyStateComponent]
})
export class InterestRateComponent implements OnInit {
  toastr = inject(ToastService);
  budgetSvc = inject(BudgetService);
  private readonly swalFire = inject(CommonFunctionsConfiguratorService);
  settingsSvc = inject(SettingsService);

  currentInterestRate: InterestRateModel[] = [];
  selectedPayment = new FormControl(null, Validators.required);
  settings!: SettingsModel;

  interestForm = new FormGroup({
    id: new UntypedFormControl(Guid.EMPTY),
    description: new FormControl('', [
      Validators.required,
      Validators.maxLength(25),
    ]),
    installments: new FormControl(0, [
      Validators.required,
      Validators.maxLength(2),
      Validators.min(1),
    ]),
    interestRate: new FormControl(0, [
      Validators.required,
      Validators.maxLength(4),
    ]),
    isActive: new FormControl(true),
  });

  listPriceForm = new FormGroup({
    values: new FormControl('', Validators.required),
  });

  ngOnInit() {
    this.getForm();
    this.getAllInterest();
    this.getListPriceValue();
  }

  getListPriceValue() {
    this.settingsSvc
      .getSettingsValue('DefaultInterestRate')
      .subscribe((data) => {
        this.settings = data;
        this.listPriceForm.controls.values.patchValue(this.settings?.values ?? '');
      });
  }

  getForm() {
    return new FormGroup({
      id: new UntypedFormControl(Guid.EMPTY),
      description: new FormControl('', [
        Validators.required,
        Validators.maxLength(25),
      ]),
      installments: new FormControl(0, [
        Validators.required,
        Validators.maxLength(2),
      ]),
      interestRate: new FormControl(0, [
        Validators.required,
        Validators.maxLength(4),
      ]),
    });
  }

  getAllInterest() {
    this.budgetSvc.getAllInterestRate().subscribe((data) => {
      this.currentInterestRate = data;
    });
  }

  editPlan(id: Guid) {
    this.cleanForm();

    this.budgetSvc.getInterestRateById(id.toString()).subscribe((data) => {
      this.interestForm.patchValue(data);
    });
  }

  activateDeactivate(e: any) {
    e.isActive = !e.isActive;
    this.budgetSvc.activeInterestPlan(e.id, e.isActive).subscribe({
      next: (data) => {
        let msg = e.isActive ? 'activó' : 'desactivó';
        this.toastr.success(
          e.isActive ? 'Activo' : 'Inactivo',
          'El plan se ' + msg + ' correctamente.'
        );
      },
      error: (err) => {
        let msg = e.isActive ? 'activar' : 'desactivar';
        e.isActive = !e.isActive;
        this.toastr.error('Error', 'No se ha podido ' + msg + ' el plan.');
      },
    });
  }

  deletePlan(id: Guid) {
    this.swalFire
      .SwalFireQuestion(
        'Eliminación',
        '¿Desea eliminar este plan de pagos?',
        'Eliminar',
        'Cancelar'
      )
      .then((result) => {
        if (result.isConfirmed) {
          this.budgetSvc.deleteInterestPlan(id).subscribe({
            next: (result) => {
              this.toastr.success(
                'Plan eliminado',
                'El plan se ha eliminado correctamente.'
              );
              this.getAllInterest();
            },
            error: (err) => {
              this.toastr.error(
                'Error',
                'No se pudo eliminar el plan de pago.'
              );
            },
          });
        }
      });
  }

  onSubmit() {
    if (this.interestForm.valid) {
      let interestPlan = this.interestForm.value as InterestRateModel;
      if (interestPlan.id != Guid.createEmpty()) {
        this.budgetSvc.updateInterest(interestPlan).subscribe({
          next: (result) => {
            this.toastr.success(
              'Interés actualizado',
              'El interés del plan se ha actualizado correctamente.'
            );
            this.cleanForm();
            this.getAllInterest();
          },
          error: (err) => {
            this.toastr.error('Error', 'No se pudo actualizar el interés.');
          },
        });
      } else {
        this.budgetSvc.createInterest(interestPlan).subscribe({
          next: (result) => {
            this.toastr.success(
              'Plan registrado',
              'El plan de pagos se ha registrado correctamente.'
            );
            this.cleanForm();
            this.getAllInterest();
          },
          error: (err) => {
            this.toastr.error(
              'Error',
              'No se pudo registrar el plan de pagos.'
            );
          },
        });
      }
    } else {
      this.interestForm.markAllAsTouched();
    }
  }

  updateListPriceRate() {
    if (!this.listPriceForm.valid) {
      this.listPriceForm.markAllAsTouched();
    } else if (!this.settings?.id) {
      this.toastr.error('Error', 'No se encontró la configuración de la tasa de precio de lista.');
    } else {
      let listPriceRate = this.listPriceForm.controls.values.value as string;
      this.settingsSvc
        .updateSettingsValue(this.settings.id, listPriceRate)
        .subscribe({
          next: () => {
            this.toastr.success(
              'Interés de precio de lista',
              'El interés del precio de lista se ha actualizado correctamente.'
            );
            this.getListPriceValue();
          },
          error: (err) => {
            this.toastr.error(
              'Error',
              'No se pudo actualizar el interés del precio de lista.'
            );
          },
        });
    }
  }

  validateOnlyNumbers(event: KeyboardEvent) {
    let key = event.key;
    let inputElement = event.target as HTMLInputElement;

    if (
      ['e', 'E', '.', '-', '+'].includes(key) ||
      key === 'ArrowUp' ||
      key === 'ArrowDown' ||
      (key === ',' && inputElement.value.includes(',')) ||
      (key === ',' && inputElement.selectionStart === 0)
    ) {
      event.preventDefault();
    }

    const interestRateControl = this.interestForm.get('interestRate');
    if (
      interestRateControl &&
      interestRateControl.value !== null &&
      interestRateControl.value !== undefined
    ) {
      if (interestRateControl.value < 0) {
        this.interestForm.controls.interestRate.patchValue(0);
      }
      if (interestRateControl.value > 999.99) {
        this.interestForm.controls.interestRate.patchValue(999.99);
      }
    }
  }

  validateTwoDecimals(event: any) {
    let input = event.target.value;
    input = input.replace(',', '.');
    let pattern = /^\d{0,3}(\.\d{0,2})?$/;

    if (!pattern.test(input)) {
      if (input === ',') {
        return;
      }
      event.target.value = input.slice(0, -1);
    } else {
      let parts = input.split(',');
      if (parts.length > 1) {
        parts[1] = parts[1].slice(0, 2);
        event.target.value = parts.join(',');
      }
    }
  }
  validateOnlyTowNumbers(event: KeyboardEvent) {
    let key = event.key;
    if (key.toLowerCase() === 'e') {
      event.preventDefault();
      return;
    }

    if (
      !/[0-9\b\-\+]/.test(key) &&
      key !== 'ArrowUp' &&
      key !== 'ArrowDown' &&
      key !== 'Delete' &&
      key !== 'Backspace'
    ) {
      event.preventDefault();
      return;
    }

    const installmentsControl = this.interestForm.get('installments');

    if (
      installmentsControl &&
      installmentsControl.value !== null &&
      installmentsControl.value.toString().length >= 2 &&
      key !== 'Backspace' &&
      key !== 'Delete'
    ) {
      event.preventDefault();
      return;
    }

    if (
      installmentsControl &&
      installmentsControl.value !== null &&
      installmentsControl.value !== undefined &&
      installmentsControl.value !== 0 &&
      installmentsControl.value.toString().startsWith('0') &&
      key !== 'Backspace' &&
      key !== 'Delete'
    ) {
      event.preventDefault();
      return;
    }

    if (
      installmentsControl &&
      installmentsControl.value !== null &&
      installmentsControl.value !== undefined
    ) {
      if (installmentsControl.value < 0) {
        this.interestForm.controls.installments.patchValue(0);
      }
      if (installmentsControl.value > 99) {
        this.interestForm.controls.installments.patchValue(99);
      }
    }
  }

  cleanForm() {
    this.interestForm.patchValue(this.getForm().value);
    this.interestForm.updateValueAndValidity();
  }
}
